/**
 * Application Card Component
 * Displays a single application card with icon, label, and optional count
 */

import React from "react";
import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Box,
  Skeleton,
  Chip,
  Fade,
} from "@mui/material";
import { Link } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import logger from "../../../logger";

const AppCard = ({ card, index, onExternalClick }) => {

  const handleCardClick = (e) => {
    if (card.isExternal) {
      e.preventDefault();
      const url = card.externalUrl || card.url;
      console.log('External app click:', { card, url }); // Debug
      if (!url) {
        console.error('No URL found for external app', card);
        return;
      }
      if (card.openInNewTab) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        onExternalClick(url, card);
      }
    }
  };

  return (
    <Fade in timeout={800 + index * 200}>
      <Card
        sx={{
          height: { xs: "auto", sm: "220px" },
          minHeight: "220px",
          display: "flex",
          flexDirection: "column",
          background: card.gradient,
          borderRadius: 3,
          transition: "all 0.3s ease-in-out",
          border: card.isNew
            ? "2px solid rgba(14, 165, 233, 0.3)"
            : "1px solid rgba(255,255,255,0.1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: `0 12px 24px ${card.shadowColor}`,
            ...(card.isNew && { borderColor: card.iconColor }),
          },
        }}
      >
        <CardActionArea
          onClick={handleCardClick}
          {...(!card.isExternal && card.path && { component: Link, to: card.path })}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <CardContent
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              height: "100%",
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {card.icon &&
                React.createElement(card.icon, {
                  sx: {
                    fontSize: { xs: 32, sm: 40 },
                    color: card.iconColor,
                  },
                })}
              {card.isExternal && (
                <OpenInNewIcon
                  sx={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    fontSize: 16,
                    color: card.iconColor,
                    bgcolor: "white",
                    borderRadius: "50%",
                    p: 0.3,
                  }}
                />
              )}
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "rgba(0,0,0,0.87)",
                textAlign: "center",
                fontSize: { xs: "1rem", sm: "1.1rem" },
              }}
            >
              {card.label}
            </Typography>

            {card.isNew && (
              <Chip
                label="🆕 חדש"
                size="small"
                sx={{
                  bgcolor: card.iconColor,
                  color: "white",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                }}
              />
            )}

            {card.count !== undefined &&
              !card.isNew &&
              (card.loading ? (
                <Skeleton variant="text" width="40%" sx={{ mx: "auto" }} />
              ) : (
                <Typography
                  variant="h4"
                  sx={{
                    color: card.iconColor,
                    fontWeight: 700,
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                  }}
                >
                  {card.count}
                </Typography>
              ))}
          </CardContent>
        </CardActionArea>
      </Card>
    </Fade>
  );
};

export default AppCard;
